/**
 * Walking without a physics engine (docs/PHASE2_PLAN.md 2.8): a vertical capsule is a circle on the
 * ground against oriented boxes (building footprints), plus gravity, a step height and a ground
 * plane. Pure functions, no three.js: unit tested directly. Units are meters; Y up; yaw 0 faces -Z.
 */

export interface Collider {
  /** Center on the ground. */
  x: number;
  z: number;
  /** Yaw of the box in radians (same convention as placements). */
  yaw: number;
  /** Half extents along the box's own x and z. */
  hw: number;
  hd: number;
  /** Height of the top surface (meters above the ground). */
  top: number;
}

export interface Bounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export interface WalkWorld {
  colliders: readonly Collider[];
  /** The walker stays inside (an interior room). */
  bounds?: Bounds;
  /** The ground (or floor slab) height where nothing else supports the walker. */
  floorY: number;
  /** Outdoors: the height of the ground at a point (terrain, sidewalks, roads); wins over `floorY`. */
  floorAt?: (x: number, z: number) => number;
}

/** The floor under a point: the ground model outdoors, the slab indoors. */
export const floorOf = (world: WalkWorld, x: number, z: number): number =>
  world.floorAt ? world.floorAt(x, z) : world.floorY;

export interface WalkerState {
  x: number;
  y: number;
  z: number;
  vy: number;
  /** Look direction: yaw around +Y (0 faces -Z), pitch up positive. */
  yaw: number;
  pitch: number;
  onGround: boolean;
}

export interface WalkInput {
  /** -1..1, forward positive. */
  forward: number;
  /** -1..1, right positive. */
  right: number;
  run: boolean;
  jump: boolean;
}

export const WALK = {
  speed: 4.2,
  runSpeed: 8.4,
  radius: 0.4,
  eyeHeight: 1.65,
  gravity: 24,
  jumpSpeed: 7,
  /** A ledge up to this high is stepped onto instead of blocking. */
  stepHeight: 0.55,
  maxDt: 0.1,
  maxPitch: Math.PI / 2 - 0.05,
} as const;

export function newWalker(x: number, z: number, yaw: number, y = 0): WalkerState {
  return { x, y, z, vy: 0, yaw, pitch: 0, onGround: true };
}

/** A point in the box's local frame (inverse of the placement rotation). */
function toLocal(c: Collider, x: number, z: number): [number, number] {
  const dx = x - c.x;
  const dz = z - c.z;
  const cos = Math.cos(c.yaw);
  const sin = Math.sin(c.yaw);
  // Rotation about +Y by yaw maps local (lx, lz) to (lx cos + lz sin, -lx sin + lz cos).
  return [dx * cos - dz * sin, dx * sin + dz * cos];
}

function toWorldDir(c: Collider, lx: number, lz: number): [number, number] {
  const cos = Math.cos(c.yaw);
  const sin = Math.sin(c.yaw);
  return [lx * cos + lz * sin, -lx * sin + lz * cos];
}

/** Is the point over the collider's footprint (grown by `margin`)? */
export function overCollider(c: Collider, x: number, z: number, margin = 0): boolean {
  const [lx, lz] = toLocal(c, x, z);
  return Math.abs(lx) <= c.hw + margin && Math.abs(lz) <= c.hd + margin;
}

/** Support height under the walker: the highest top it may stand on (within a step), else the floor. */
export function groundHeight(world: WalkWorld, x: number, z: number, feetY: number): number {
  let ground = floorOf(world, x, z);
  for (const c of world.colliders) {
    if (c.top <= feetY + WALK.stepHeight && c.top > ground && overCollider(c, x, z)) {
      ground = c.top;
    }
  }
  return ground;
}

/** Pushes the circle at (x, z) out of every blocking box; a few passes settle corners. */
export function resolveHorizontal(
  world: WalkWorld,
  x: number,
  z: number,
  feetY: number,
  radius: number = WALK.radius,
): [number, number] {
  let px = x;
  let pz = z;
  for (let pass = 0; pass < 3; pass++) {
    let moved = false;
    for (const c of world.colliders) {
      if (c.top <= feetY + WALK.stepHeight) continue; // low enough to step onto
      const [lx, lz] = toLocal(c, px, pz);
      const cx = Math.max(-c.hw, Math.min(c.hw, lx));
      const cz = Math.max(-c.hd, Math.min(c.hd, lz));
      let nx = lx - cx;
      let nz = lz - cz;
      let dist = Math.hypot(nx, nz);
      if (dist >= radius) continue;
      if (dist < 1e-9) {
        // Center inside the box: leave through the nearest face.
        const dxFace = c.hw - Math.abs(lx);
        const dzFace = c.hd - Math.abs(lz);
        if (dxFace < dzFace) {
          nx = Math.sign(lx || 1);
          nz = 0;
          dist = -dxFace;
        } else {
          nx = 0;
          nz = Math.sign(lz || 1);
          dist = -dzFace;
        }
      } else {
        nx /= dist;
        nz /= dist;
      }
      const push = radius - dist;
      const [wx, wz] = toWorldDir(c, nx * push, nz * push);
      px += wx;
      pz += wz;
      moved = true;
    }
    if (!moved) break;
  }
  const b = world.bounds;
  if (b) {
    px = Math.max(b.minX + radius, Math.min(b.maxX - radius, px));
    pz = Math.max(b.minZ + radius, Math.min(b.maxZ - radius, pz));
  }
  return [px, pz];
}

/** Advances the walker by `dt` seconds. Returns a new state. */
export function stepWalker(
  state: WalkerState,
  input: WalkInput,
  dtRaw: number,
  world: WalkWorld,
): WalkerState {
  const dt = Math.max(0, Math.min(WALK.maxDt, dtRaw));
  const len = Math.hypot(input.forward, input.right);
  const scale = len > 1 ? 1 / len : 1;
  const speed = input.run ? WALK.runSpeed : WALK.speed;
  const fx = -Math.sin(state.yaw);
  const fz = -Math.cos(state.yaw);
  const rx = Math.cos(state.yaw);
  const rz = -Math.sin(state.yaw);
  const dx = (fx * input.forward + rx * input.right) * scale * speed * dt;
  const dz = (fz * input.forward + rz * input.right) * scale * speed * dt;
  // Two sub-steps keep a fast walker (running at a low frame rate) from tunnelling a thin wall.
  const [hx, hz] = resolveHorizontal(world, state.x + dx / 2, state.z + dz / 2, state.y);
  const [x, z] = resolveHorizontal(world, hx + dx / 2, hz + dz / 2, state.y);

  let vy = state.vy;
  let y = state.y;
  if (state.onGround && input.jump) vy = WALK.jumpSpeed;
  vy -= WALK.gravity * dt;
  y += vy * dt;
  const ground = groundHeight(world, x, z, state.y);
  let onGround = false;
  if (y <= ground) {
    y = ground;
    vy = 0;
    onGround = true;
  } else if (state.onGround && !input.jump && state.y - ground <= WALK.stepHeight) {
    // Walking off a low ledge: stay glued to the ground instead of a tiny fall.
    y = ground;
    vy = 0;
    onGround = true;
  }
  return { ...state, x, z, y, vy, onGround };
}

/** Look with mouse or touch deltas (radians), clamping pitch. */
export function look(state: WalkerState, dYaw: number, dPitch: number): WalkerState {
  return {
    ...state,
    yaw: state.yaw + dYaw,
    pitch: Math.max(-WALK.maxPitch, Math.min(WALK.maxPitch, state.pitch + dPitch)),
  };
}

// ------------------------------------------------------------------ third person and fly

/** Camera position behind the walker for the third-person view (never below the ground). */
export function thirdPersonCamera(
  state: WalkerState,
  distance = 4.4,
  height = 1.55,
  shoulder = 0.6,
): { position: [number, number, number]; target: [number, number, number] } {
  const cp = Math.cos(state.pitch);
  // Over the shoulder: camera and target both shift to the player's right, so the character sits
  // in the left third of the view and the road ahead stays in the middle.
  const rx = Math.cos(state.yaw) * shoulder;
  const rz = -Math.sin(state.yaw) * shoulder;
  const target: [number, number, number] = [state.x + rx, state.y + height, state.z + rz];
  const position: [number, number, number] = [
    target[0] + Math.sin(state.yaw) * cp * distance,
    Math.max(state.y + 0.4, target[1] + Math.sin(state.pitch) * distance),
    target[2] + Math.cos(state.yaw) * cp * distance,
  ];
  return { position, target };
}

export interface FlyState {
  x: number;
  y: number;
  z: number;
  yaw: number;
  pitch: number;
}

export interface FlyInput {
  forward: number;
  right: number;
  /** -1 down .. 1 up. */
  up: number;
  fast: boolean;
}

export const FLY = { speed: 25, fastSpeed: 90, minY: 1, maxY: 1500 } as const;

/** Free flight: forward follows the look direction including pitch. */
export function stepFly(state: FlyState, input: FlyInput, dtRaw: number): FlyState {
  const dt = Math.max(0, Math.min(WALK.maxDt, dtRaw));
  const speed = (input.fast ? FLY.fastSpeed : FLY.speed) * dt;
  const cp = Math.cos(state.pitch);
  const fx = -Math.sin(state.yaw) * cp;
  const fy = Math.sin(state.pitch);
  const fz = -Math.cos(state.yaw) * cp;
  const rx = Math.cos(state.yaw);
  const rz = -Math.sin(state.yaw);
  return {
    ...state,
    x: state.x + (fx * input.forward + rx * input.right) * speed,
    y: Math.max(FLY.minY, Math.min(FLY.maxY, state.y + (fy * input.forward + input.up) * speed)),
    z: state.z + (fz * input.forward + rz * input.right) * speed,
  };
}
